import { NavLink, Outlet } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/state/AuthContext";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";

const adminLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "block px-3 py-2 text-sm rounded-md transition-colors",
    isActive ? "bg-[#1e3a4f] text-white" : "text-[#1e3a4f] hover:bg-[#1e3a4f]/10",
  );

const platformLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "block px-3 py-2 text-sm font-semibold rounded-md transition-colors",
    isActive ? "bg-red-600 text-white" : "text-red-600 hover:bg-red-50",
  );

const ADMIN_LINKS: Array<{
  to: string;
  label: string;
  permission?: string;
  anyPermission?: string[];
}> = [
  { to: "/admin/team", label: "Team", permission: PERMISSIONS.adminUsers },
  { to: "/admin/billing", label: "Billing", permission: PERMISSIONS.billing },
  { to: "/admin/users", label: "Users", permission: PERMISSIONS.adminUsers },
  { to: "/admin/roles", label: "Roles", permission: "admin_roles_access" },
  { to: "/admin/systems", label: "Systems", permission: PERMISSIONS.adminSystems },
  { to: "/admin/parts", label: "Parts", permission: PERMISSIONS.adminParts },
  { to: "/admin/locations", label: "Locations", permission: PERMISSIONS.adminParts },
  {
    to: "/admin/custom-fields",
    label: "Custom Fields",
    anyPermission: [PERMISSIONS.adminParts, PERMISSIONS.adminSystems],
  },
  { to: "/admin/transaction-types", label: "Transaction Types", permission: "admin_transaction_types_access" },
  { to: "/admin/receipt-options", label: "Receiving Options", permission: "admin_transaction_types_access" },
  { to: "/admin/request-options", label: "Shipping Options", permission: "admin_transaction_types_access" },
  { to: "/admin/customers", label: "Customers", permission: "admin_customers_access" },
  { to: "/admin/maintenance", label: "Maintenance", permission: "admin_maintenance_access" },
  { to: "/admin/mail", label: "Email Settings", permission: "admin_email_notifications_access" },
] as const;

function canSeeAdminLink(
  user: ReturnType<typeof useAuth>["user"],
  link: (typeof ADMIN_LINKS)[number],
): boolean {
  if (link.anyPermission) {
    return link.anyPermission.some((permission) => hasPermission(user, permission));
  }
  return link.permission ? hasPermission(user, link.permission) : false;
}

export function AdminLayout() {
  const { user } = useAuth();

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <aside className="md:w-48 shrink-0">
        <nav className="bg-white rounded-lg shadow p-2 space-y-0.5" aria-label="Admin menu">
          {ADMIN_LINKS.filter((l) => canSeeAdminLink(user, l)).map((link) => (
            <NavLink key={link.to} to={link.to} className={adminLinkClass}>
              {link.label}
            </NavLink>
          ))}
          {user?.isPlatformAdmin && (
            <NavLink to="/admin/platform" className={platformLinkClass}>
              Platform
            </NavLink>
          )}
        </nav>
      </aside>
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
